// client/src/pages/RestaurantePage.jsx
import React, { useState, useEffect, useRef } from "react";
import { getRestaurantes, createRestaurante, updateRestaurante, deleteRestaurante, } from "../api/restaurantes.js";
import { getEstablecimientos, createEstablecimiento, updateEstablecimiento, deleteEstablecimiento } from "../api/establecimientos.js";
import InputGroup from "../components/InputGroup.js";
import SortableHeader from "../components/SortableHeader.js";
import { useSortableData } from "../hooks/useSortableData.js";
import { usePagination } from "../hooks/usePagination.js";
import { useAuth } from "../context/AuthContext.js";
import Pagination from "../components/Pagination";
const API_URL = process.env.REACT_APP_API_URL || "http://localhost:4000";

const RestaurantePage = () => {
  const { usuario } = useAuth();
  const [restaurantes, setRestaurantes] = useState([]);
  const [filteredRestaurantes, setFilteredRestaurantes] = useState([]);
  const [searchTerm, setSearchTerm] = useState(""); 
  const inputRefs = useRef({}); // aquí guardaremos los refs dinámicamente 
  // Hook de ordenamiento reutilizable
  const { sortedItems: sortedRestaurantes, sortConfig, setSortConfig } = useSortableData(filteredRestaurantes);
  // Hook de paginación
  const { currentPage, setCurrentPage, paginatedItems: currentRestaurantes, totalPages, itemsPerPage } = usePagination(sortedRestaurantes, 20);
  const [restaurante, setRestaurante] = useState({ razon_social: "", nombre_comercial: "", ruc: "", direccion_matriz: "", obligado_contabilidad: "NO", iva_vigente: 12.0, ambiente_sri: "1", eslogan: "", direccion: "", telefono: "", correo: "", logo: null, portada: null, mision: "", vision: "", valores: "", historia: "", resena_general: 0, latitud: "", longitud: "", horario_apertura: "", horario_cierre: "", estado: "activo", });
  const [previewLogo, setPreviewLogo] = useState(null);
  const [previewPortada, setPreviewPortada] = useState(null);
  const [errors, setErrors] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  // Estados para establecimientos
  const [establecimientos, setEstablecimientos] = useState([]);
  const [establecimiento, setEstablecimiento] = useState({ estab: "", ptoemi: "", direstablecimiento: "", latitud: "", longitud: "" });
  const [editandoEstablecimiento, setEditandoEstablecimiento] = useState(null);
  const [mostrarFormEstablecimiento, setMostrarFormEstablecimiento] = useState(false);

  // Estados para certificado digital
  const [certificadoFile, setCertificadoFile] = useState(null);
  const [certificadoPassword, setCertificadoPassword] = useState("");
  const [infoCertificado, setInfoCertificado] = useState(null);
  const [subiendoCertificado, setSubiendoCertificado] = useState(false);

  // 🔹 Cargar todos los restaurantes
  const loadRestaurantes = async () => {
    try {
      const data = await getRestaurantes();
      const list = data.restaurantes || [];
      setRestaurantes(list);
      setFilteredRestaurantes(list);
    } catch (err) {
      console.error("Error al cargar restaurantes:", err);
    }
  };

  useEffect(() => {
    loadRestaurantes();
  }, []);

  // 🔹 Filtro general
  useEffect(() => {
    const filtered = restaurantes.filter((r) =>
      Object.values(r).some((value) =>
        String(value).toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
    setFilteredRestaurantes(filtered);
  }, [searchTerm, restaurantes]);

  const formatTime = (time) => {
    if (!time) return "";
    // Si viene en formato HH:mm:ss, solo toma HH:mm
    return time.length === 8 ? time.slice(0, 5) : time;
  };

  // Manejar cambios en inputs
  const handleChange = (e) => {
    const { name, value } = e.target;
    setRestaurante({ ...restaurante, [name]: value });
  };

    // Resetear formulario
// Resetear formulario
const resetForm = () => {
  setRestaurante({ razon_social: "", nombre_comercial: "", ruc: "", direccion_matriz: "", obligado_contabilidad: "NO", iva_vigente: 12.0, ambiente_sri: "1", eslogan: "", direccion: "", telefono: "", correo: "", logo: null, portada: null, mision: "", vision: "", valores: "", historia: "", resena_general: 0, latitud: "", longitud: "", horario_apertura: "", horario_cierre: "", estado: "activo", });
  setPreviewLogo(null);
  // Limpiar estados de certificado
  setInfoCertificado(null);
  setCertificadoFile(null);
  setCertificadoPassword("");
  const fileInput = document.querySelector('input[type="file"][accept=".p12,.pfx"]');
  if (fileInput) fileInput.value = '';
  setPreviewPortada(null);
  setEditMode(false);
  setErrors([]);
  
  // Limpiar estados de establecimientos
  setEstablecimientos([]);
  setEstablecimiento({ estab: "", ptoemi: "", direstablecimiento: "", latitud: "", longitud: "" });
  setMostrarFormEstablecimiento(false);
  setEditandoEstablecimiento(null);
};


  // Manejar selección de archivos
  const handleFileChange = (e) => {
    const { name, files } = e.target;
    if (files.length > 0) {
      setRestaurante({ ...restaurante, [name]: files[0] });
      if (name === "logo") setPreviewLogo(URL.createObjectURL(files[0]));
      if (name === "portada") setPreviewPortada(URL.createObjectURL(files[0]));
    }
  };

  const validateRestaurante = (r) => {
    const errors = {};

    // Información general
    if (!r.razon_social.trim()) errors.razon_social = "La razón social es obligatoria";
    if (!r.nombre_comercial.trim()) errors.nombre_comercial = "El nombre comercial es obligatorio";
    if (!r.ruc.trim()) errors.ruc = "El RUC es obligatorio";
    if (!r.direccion_matriz.trim()) errors.direccion_matriz = "La dirección matriz es obligatoria";
    if (!r.obligado_contabilidad.trim()) errors.obligado_contabilidad = "El campo 'obligado a contabilidad' es obligatorio";
    if (r.iva_vigente === "" || r.iva_vigente === null)
      errors.iva_vigente = "El IVA vigente es obligatorio";
    if (!r.eslogan.trim()) errors.eslogan = "El eslogan es obligatorio";

    // Contacto y ubicación
    if (!r.direccion.trim()) errors.direccion = "La dirección es obligatoria";
    if (!r.telefono.trim()) errors.telefono = "El teléfono es obligatorio";
    if (!r.correo.trim()) errors.correo = "El correo es obligatorio";
    if (!r.latitud.trim()) errors.latitud = "La latitud es obligatoria";
    if (!r.longitud.trim()) errors.longitud = "La longitud es obligatoria";

    // Validación de horarios (solo HH:mm)
    const timeRegex = /^([01]\d|2[0-3]):([0-5]\d)$/;
    if (!r.horario_apertura.trim()) {
      errors.horario_apertura = "El horario de apertura es obligatorio";
    } else if (!timeRegex.test(r.horario_apertura)) {
      errors.horario_apertura = "El horario de apertura debe estar en formato HH:mm (ej. 07:00)";
    }

    if (!r.horario_cierre.trim()) {
      errors.horario_cierre = "El horario de cierre es obligatorio";
    } else if (!timeRegex.test(r.horario_cierre)) {
      errors.horario_cierre = "El horario de cierre debe estar en formato HH:mm (ej. 21:00)";
    }

    //  Información institucional
    if (!r.mision.trim()) errors.mision = "La misión es obligatoria";
    if (!r.vision.trim()) errors.vision = "La visión es obligatoria";
    if (!r.valores.trim()) errors.valores = "Los valores son obligatorios";
    if (!r.historia.trim()) errors.historia = "La historia es obligatoria";

    // Configuración
    if (!r.estado.trim()) errors.estado = "El estado es obligatorio";

    return errors;
  };

// Manejar envío del formulario
const handleSubmit = async (e) => {
  e.preventDefault();
  const validationErrors = validateRestaurante(restaurante);
  
  if (Object.keys(validationErrors).length > 0) {
    setErrors(validationErrors);
    const firstErrorField = Object.keys(validationErrors)[0];
    const fieldRef = inputRefs.current[firstErrorField];
    if (fieldRef && fieldRef.scrollIntoView) {
      fieldRef.scrollIntoView({ behavior: "smooth", block: "center" });
      fieldRef.focus();
    }
    return;
  }

  try {
    let restauranteGuardado;
    
    if (editMode) {
      restauranteGuardado = await updateRestaurante(restaurante.id_restaurante, restaurante);
      alert("Restaurante actualizado correctamente");
    } else {
      restauranteGuardado = await createRestaurante(restaurante);
      alert("Restaurante creado correctamente");
    }

    // Guardar establecimientos
    if (establecimientos.length > 0) {
      const idRestaurante = restauranteGuardado.restaurante?.id_restaurante || 
      restauranteGuardado.id_restaurante || 
      restaurante.id_restaurante;

      if (!idRestaurante) {
        throw new Error("No se pudo obtener el ID del restaurante");
      }
      
      await guardarEstablecimientos(idRestaurante);
    }

    resetForm();
    setModalOpen(false);
    loadRestaurantes();
  } catch (err) {
    console.error("Error al guardar restaurante:", err);
    
    // Extraer el mensaje de error del backend
    let errorMessage = "Error desconocido";
    
    if (err.response?.data) {
      errorMessage = err.response.data.error || 
                    err.response.data.message || 
                    JSON.stringify(err.response.data);
    } else if (err.message) {
      errorMessage = err.message;
    }
    
    setErrors({ general: errorMessage });
    alert(errorMessage);
  }
};

// Editar restaurante
const handleEdit = async (r) => {
  setRestaurante({ ...r, logo: null, portada: null, 
    horario_apertura: formatTime(r.horario_apertura),
    horario_cierre: formatTime(r.horario_cierre), 
  });
  setPreviewLogo(r.logo ? `${API_URL}${r.logo}` : null);
  setPreviewPortada(r.portada ? `${API_URL}${r.portada}` : null);
  setEditMode(true);
  setModalOpen(true);
  await cargarEstablecimientos(r.id_restaurante);
  await cargarInfoCertificado(r.id_restaurante);
};


  // Eliminar restaurante
  const handleDelete = async (id) => {
    if (window.confirm("¿Estás seguro de eliminar este restaurante?")) {
      try {
        await deleteRestaurante(id);
        alert(" Restaurante eliminado correctamente");
        loadRestaurantes();
      } catch {
        alert(" Error al eliminar restaurante");
      }
    }
  };

  // 🔐 Funciones para gestionar certificado digital
  const cargarInfoCertificado = async (id_restaurante) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/certificados/${id_restaurante}/info`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setInfoCertificado(data);
    } catch (error) {
      console.error("Error al cargar info de certificado:", error);
      setInfoCertificado(null);
    }
  };

  const handleSubirCertificado = async () => {
    if (!certificadoFile) {
      alert("Debe seleccionar un archivo .p12");
      return;
    }
    
    if (!certificadoPassword) {
      alert("Debe ingresar la contraseña del certificado");
      return;
    }

    if (!restaurante.id_restaurante) {
      alert("Debe guardar el restaurante primero");
      return;
    }

    setSubiendoCertificado(true);

    try {
      const formData = new FormData();
      formData.append('certificado', certificadoFile);
      formData.append('password', certificadoPassword);

      const token = localStorage.getItem("token");
      const response = await fetch(
        `${API_URL}/api/certificados/${restaurante.id_restaurante}/subir`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData
        }
      );

      const data = await response.json();

      if (response.ok) {
        alert("✅ Certificado subido exitosamente");
        // Limpiar formulario
        setCertificadoFile(null);
        setCertificadoPassword("");
        // Limpiar input file
        const fileInput = document.querySelector('input[type="file"][accept=".p12,.pfx"]');
        if (fileInput) fileInput.value = '';
        // Recargar info del certificado
        await cargarInfoCertificado(restaurante.id_restaurante);
      } else {
        alert(`❌ Error: ${data.message || 'Error al subir certificado'}`);
      }
    } catch (error) {
      console.error("Error al subir certificado:", error);
      alert("❌ Error al subir certificado");
    } finally {
      setSubiendoCertificado(false);
    }
  };

  const handleEliminarCertificado = async () => {
    if (!window.confirm("¿Está seguro de eliminar el certificado digital?")) {
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${API_URL}/api/certificados/${restaurante.id_restaurante}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const data = await response.json();

      if (response.ok) {
        alert("✅ Certificado eliminado exitosamente");
        // Limpiar estado inmediatamente
        setInfoCertificado({ tieneCertificado: false, nombreArchivo: null, password: null });
        // Recargar info del certificado
        await cargarInfoCertificado(restaurante.id_restaurante);
      } else {
        alert(`❌ Error: ${data.message || 'Error al eliminar certificado'}`);
      }
    } catch (error) {
      console.error("Error al eliminar certificado:", error);
      alert("❌ Error al eliminar certificado");
    }
  };

  // Agregar establecimiento a la lista temporal
const agregarEstablecimiento = () => {
  const erroresEstab = {};
  if (!establecimiento.estab.trim()) erroresEstab.estab = "Requerido";
  if (!establecimiento.ptoemi.trim()) erroresEstab.ptoemi = "Requerido";
  if (!establecimiento.direstablecimiento.trim()) erroresEstab.direstablecimiento = "Requerido";
  
  if (Object.keys(erroresEstab).length > 0) {
    setErrors({ ...errors, ...erroresEstab });
    return;
  }

  if (editandoEstablecimiento !== null) {
    // Editar establecimiento existente
    const nuevosEstablecimientos = [...establecimientos];
    nuevosEstablecimientos[editandoEstablecimiento] = establecimiento;
    setEstablecimientos(nuevosEstablecimientos);
    setEditandoEstablecimiento(null);
  } else {
    // Agregar nuevo establecimiento
    setEstablecimientos([...establecimientos, establecimiento]);
  }

    // Limpiar formulario
    setEstablecimiento({ estab: "", ptoemi: "", direstablecimiento: "", latitud: "", longitud: "" });
    setMostrarFormEstablecimiento(false);
  };

  // Editar establecimiento de la lista
  const editarEstablecimiento = (index) => {
    setEstablecimiento(establecimientos[index]);
    setEditandoEstablecimiento(index);
    setMostrarFormEstablecimiento(true);
  };

  // Eliminar establecimiento de la lista
  const eliminarEstablecimiento = (index) => {
    const nuevosEstablecimientos = establecimientos.filter((_, i) => i !== index);
    setEstablecimientos(nuevosEstablecimientos);
  };

  // Guardar establecimientos en el backend después de crear/editar restaurante
  const guardarEstablecimientos = async (id_restaurante) => {
    try {
      // Obtener establecimientos existentes del backend
      const data = await getEstablecimientos();
      const establecimientosExistentes = data.filter(e => e.id_restaurante === id_restaurante);
      
      // Eliminar establecimientos que ya no están en la lista
      for (const existente of establecimientosExistentes) {
        const sigueEnLista = establecimientos.find(e => e.id === existente.id);
        if (!sigueEnLista) {
          await deleteEstablecimiento(existente.id);
        }
      }
      
      // Crear o actualizar establecimientos
      for (const estab of establecimientos) {
        if (estab.id) {
          // Actualizar establecimiento existente
          await updateEstablecimiento(estab.id, { ...estab, id_restaurante });
        } else {
          // Crear nuevo establecimiento
          await createEstablecimiento({ ...estab, id_restaurante });
        }
      }
    } catch (err) {
      console.error("Error al guardar establecimientos:", err);
      throw err;
    }
  };

// Cargar establecimientos de un restaurante específico
const cargarEstablecimientos = async (id_restaurante) => {
  try {
    const data = await getEstablecimientos();
    // Filtrar solo los establecimientos de este restaurante
    const establecimientosRestaurante = data.filter(e => e.id_restaurante === id_restaurante);
    setEstablecimientos(establecimientosRestaurante);
  } catch (err) {
    console.error("Error al cargar establecimientos:", err);
  }
};

  return (
    <div className="p-6 max-w-[90rem] mx-auto">
      <h1 className="text-3xl font-bold mb-6 text-center"> Gestión de Restaurantes </h1>

      {/* Buscador + Botón */}
      <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
        <input type="text" placeholder="🔍 Buscar por cualquier campo..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="border p-2 rounded w-full md:w-1/2" />
        {usuario?.isSuperAdmin && (
          <button onClick={() => { resetForm(); setModalOpen(true); }} className="bg-green-600 text-white px-6 py-2 rounded hover:bg-green-700" > ➕ Nuevo Restaurante </button>
        )}
      </div>

      {/* Tabla */}
      <div className="overflow-x-auto bg-white shadow-md rounded-xl w-full">
        <table className="min-w-full text-sm text-left divide-y divide-gray-200">
          <thead className="bg-gray-100 text-gray-700 font-semibold">
            <tr className="cursor-pointer">
              <SortableHeader label="N" sortKey="id_restaurante" sortConfig={sortConfig} setSortConfig={setSortConfig} />
              <SortableHeader label="Razón Social" sortKey="razon_social" sortConfig={sortConfig} setSortConfig={setSortConfig} />
              <SortableHeader label="Nombre Comercial" sortKey="nombre_comercial" sortConfig={sortConfig} setSortConfig={setSortConfig} />
              <SortableHeader label="RUC" sortKey="ruc" sortConfig={sortConfig} setSortConfig={setSortConfig} />
              <SortableHeader label="Teléfono" sortKey="telefono" sortConfig={sortConfig} setSortConfig={setSortConfig} />
              <SortableHeader label="Correo" sortKey="correo" sortConfig={sortConfig} setSortConfig={setSortConfig} />
              <SortableHeader label="Estado" sortKey="estado" sortConfig={sortConfig} setSortConfig={setSortConfig} />
              <th className="text-center">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {currentRestaurantes.length > 0 ? (
              currentRestaurantes.map((r, idx) => (
                <tr key={r.id_restaurante} className="hover:bg-gray-50">
                  <td className="px-4 py-2">{(currentPage - 1) * itemsPerPage + idx + 1}</td>
                  <td className="px-4 py-2">{r.razon_social}</td>
                  <td className="px-4 py-2">{r.nombre_comercial}</td>
                  <td className="px-4 py-2">{r.ruc}</td>
                  <td className="px-4 py-2">{r.telefono || "-"}</td>
                  <td className="px-4 py-2">{r.correo || "-"}</td>
                  <td className={`px-4 py-2 text-center font-semibold ${r.estado === "activo" ? "text-green-600" : "text-red-500"}`}>
                    {r.estado}
                  </td>
                  <td className="px-4 py-2 text-center flex justify-center gap-2">
                    <button className="bg-yellow-400 text-white px-3 py-1 rounded hover:bg-yellow-500" onClick={() => handleEdit(r)}>Editar</button>
                    <button className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600" onClick={() => handleDelete(r.id_restaurante)}>Eliminar</button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8" className="text-center py-4 text-gray-500">No hay restaurantes registrados</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Paginación */}
      <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />

      {/* Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-lg w-[70%] max-h-[90vh] overflow-y-auto p-6 relative">
            <button onClick={() => setModalOpen(false)} className="absolute top-2 right-3 text-gray-500 hover:text-black" > ✖  </button>
            <h2 className="text-2xl font-semibold mb-6 text-center sticky top-0 bg-white pb-2"> {editMode ? "Editar Restaurante" : "Nuevo Restaurante"} </h2>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Mensaje de error general */}
              {errors.general && (
                <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-red-700 font-medium">
                        {errors.general}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setErrors({ ...errors, general: null })}
                      className="ml-auto text-red-500 hover:text-red-700"
                    >
                      ✖
                    </button>
                  </div>
                </div>
              )}
              
              {/* Datos Generales */}
              <h3 className="text-lg font-semibold text-gray-700"> Datos Generales </h3> 
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InputGroup label="Razón Social" error={errors.razon_social}> <input name="razon_social" value={restaurante.razon_social} onChange={handleChange} placeholder="Ingrese la razón social" className={`input-base ${ errors.razon_social ? "border-red-500 focus:ring-red-400" : "" }`} ref={(el) => (inputRefs.current["razon_social"] = el)}/> </InputGroup>
                <InputGroup label="Nombre Comercial" error={errors.nombre_comercial}> <input name="nombre_comercial" value={restaurante.nombre_comercial} onChange={handleChange} placeholder="Ingrese el nombre comercial" className={`input-base ${ errors.nombre_comercial ? "border-red-500 focus:ring-red-400" : "" }`}ref={(el) => (inputRefs.current["nombre_comercial"] = el)} /> </InputGroup>
                <InputGroup label="RUC" error={errors.ruc}> <input name="ruc" value={restaurante.ruc} onChange={handleChange} placeholder="Ingrese el RUC" className={`input-base ${errors.ruc ? "border-red-500 focus:ring-red-400" : ""}`} ref={(el) => (inputRefs.current["ruc"] = el)}/> </InputGroup>
                <InputGroup label="Dirección Matriz" error={errors.direccion_matriz}> <input name="direccion_matriz" value={restaurante.direccion_matriz} onChange={handleChange} placeholder="Ingrese dirección matriz" className={`input-base ${ errors.direccion_matriz ? "border-red-500 focus:ring-red-400" : "" }`} ref={(el) => (inputRefs.current["direccion_matriz"] = el)}/> </InputGroup>
                <InputGroup label="Obligado a contabilidad" error={errors.obligado_contabilidad}> <select name="obligado_contabilidad" value={restaurante.obligado_contabilidad} onChange={handleChange} className={`input-base ${ errors.obligado_contabilidad ? "border-red-500 focus:ring-red-400" : "" }`} > <option value="SI">Sí</option> <option value="NO">No</option> </select> </InputGroup>
                <InputGroup label="IVA Vigente (%)" error={errors.iva_vigente}> <input type="number" name="iva_vigente" value={restaurante.iva_vigente} onChange={handleChange} placeholder="12%" className={`input-base ${ errors.iva_vigente ? "border-red-500 focus:ring-red-400" : "" }`}ref={(el) => (inputRefs.current["iva_vigente"] = el)} /> </InputGroup>
                <InputGroup label="Ambiente SRI (Facturación Electrónica)" error={errors.ambiente_sri}>
                  <select 
                    name="ambiente_sri" 
                    value={restaurante.ambiente_sri} 
                    onChange={handleChange} 
                    className={`input-base ${ errors.ambiente_sri ? "border-red-500 focus:ring-red-400" : "" }`}
                  >
                    <option value="1">Pruebas</option>
                    <option value="2">Producción</option>
                  </select>
                  <small className="text-gray-600 mt-1 block">
                    Selecciona el ambiente del SRI para facturación electrónica. Usa "Pruebas" para testing y "Producción" para facturas reales.
                  </small>
                </InputGroup>
                <InputGroup label="Eslogan" error={errors.eslogan}> <input name="eslogan" value={restaurante.eslogan} onChange={handleChange} placeholder="Ingrese un eslogan" className={`input-base col-span-2 ${ errors.eslogan ? "border-red-500 focus:ring-red-400" : "" }`}ref={(el) => (inputRefs.current["eslogan"] = el)} /> </InputGroup>
              </div>

              {/* Contacto y Ubicación */}
              <h3 className="text-lg font-semibold text-gray-700"> Contacto y Ubicación </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InputGroup label="Dirección" error={errors.direccion}> <input name="direccion" value={restaurante.direccion} onChange={handleChange} placeholder="Ingrese dirección" className={`input-base ${ errors.direccion ? "border-red-500 focus:ring-red-400" : "" }`} ref={(el) => (inputRefs.current["direccion"] = el)}/> </InputGroup>
                <InputGroup label="Teléfono" error={errors.telefono}> <input name="telefono" value={restaurante.telefono} onChange={handleChange} placeholder="Ingrese teléfono" className={`input-base ${ errors.telefono ? "border-red-500 focus:ring-red-400" : "" }`} ref={(el) => (inputRefs.current["telefono"] = el)}/> </InputGroup>
                <InputGroup label="Correo" error={errors.correo}> <input name="correo" value={restaurante.correo} onChange={handleChange} placeholder="Ingrese correo" className={`input-base ${ errors.correo ? "border-red-500 focus:ring-red-400" : "" }`} ref={(el) => (inputRefs.current["correo"] = el)}/> </InputGroup>
                <InputGroup label="Latitud" error={errors.latitud}> <input name="latitud" value={restaurante.latitud} onChange={handleChange} placeholder="Latitud" className={`input-base ${ errors.latitud ? "border-red-500 focus:ring-red-400" : "" }`} ref={(el) => (inputRefs.current["latitud"] = el)}/> </InputGroup>
                <InputGroup label="Longitud" error={errors.longitud}> <input name="longitud" value={restaurante.longitud} onChange={handleChange} placeholder="Longitud" className={`input-base ${ errors.longitud ? "border-red-500 focus:ring-red-400" : "" }`} ref={(el) => (inputRefs.current["longitud"] = el)}/> </InputGroup>
              </div>

              {/* Archivos */}
              <h3 className="text-lg font-semibold text-gray-700">Imágenes</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InputGroup label="Logo" error={errors.logo}> <input type="file" name="logo" accept="image/*" onChange={handleFileChange} className={`input-base w-full ${ errors.logo ? "border-red-500 focus:ring-red-400" : "" }`} /> {previewLogo && ( <img src={previewLogo} alt="Logo" className="mt-2 w-32 h-32 object-contain border rounded" /> )} </InputGroup>
                <InputGroup label="Portada" error={errors.portada}> <input type="file" name="portada" accept="image/*" onChange={handleFileChange} className={`input-base w-full ${ errors.portada ? "border-red-500 focus:ring-red-400" : "" }`} /> {previewPortada && ( <img src={previewPortada} alt="Portada" className="mt-2 w-full h-32 object-cover rounded" /> )} </InputGroup>
              </div>

              {/* Información Institucional */}
              <h3 className="text-lg font-semibold text-gray-700">Información Institucional</h3>
              <div className="grid grid-cols-1 gap-4">
                <InputGroup label="Misión" error={errors.mision}> <textarea name="mision" value={restaurante.mision} onChange={handleChange} rows={2} className={`input-base ${errors.mision ? "border-red-500 focus:ring-red-400" : ""}`} placeholder="Ingrese la misión" ref={(el) => (inputRefs.current["mision"] = el)}/> </InputGroup>
                <InputGroup label="Visión" error={errors.vision}> <textarea name="vision" value={restaurante.vision} onChange={handleChange} rows={2} className={`input-base ${errors.vision ? "border-red-500 focus:ring-red-400" : ""}`} placeholder="Ingrese la visión" ref={(el) => (inputRefs.current["vision"] = el)}/> </InputGroup>
                <InputGroup label="Valores" error={errors.valores}> <textarea name="valores" value={restaurante.valores} onChange={handleChange} rows={2} className={`input-base ${errors.valores ? "border-red-500 focus:ring-red-400" : ""}`} placeholder="Ingrese los valores" ref={(el) => (inputRefs.current["valores"] = el)}/> </InputGroup>
                <InputGroup label="Historia" error={errors.historia}> <textarea name="historia" value={restaurante.historia} onChange={handleChange} rows={2} className={`input-base ${errors.historia ? "border-red-500 focus:ring-red-400" : ""}`} placeholder="Ingrese la historia" ref={(el) => (inputRefs.current["historia"] = el)}/> </InputGroup>
              </div>

              {/* Configuración */}
              <h3 className="text-lg font-semibold text-gray-700">Configuración</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InputGroup label="Horario de Apertura" error={errors.horario_apertura}> <input name="horario_apertura" value={restaurante.horario_apertura} onChange={handleChange} placeholder="hh:mm" className={`input-base ${ errors.horario_apertura ? "border-red-500 focus:ring-red-400" : "" }`} ref={(el) => (inputRefs.current["horario_apertura"] = el)}/> </InputGroup>
                <InputGroup label="Horario de Cierre" error={errors.horario_cierre}> <input name="horario_cierre" value={restaurante.horario_cierre} onChange={handleChange} placeholder="hh:mm" className={`input-base ${ errors.horario_cierre ? "border-red-500 focus:ring-red-400" : "" }`} ref={(el) => (inputRefs.current["horario_cierre"] = el)} /> </InputGroup>
                <InputGroup label="Reseña General" error={errors.resena_general}> <input type="number" name="resena_general" value={restaurante.resena_general} onChange={handleChange} placeholder="Ingrese la reseña general" className={`input-base ${ errors.resena_general ? "border-red-500 focus:ring-red-400" : "" }`} ref={(el) => (inputRefs.current["resena_general"] = el)}/> </InputGroup>
                <InputGroup label="Estado" error={errors.estado}> <select name="estado" value={restaurante.estado} onChange={handleChange} className={`input-base ${ errors.estado ? "border-red-500 focus:ring-red-400" : "" }`} > <option value="activo">Activo</option> <option value="inactivo">Inactivo</option> </select> </InputGroup>
              </div>

              {/* Sección de Establecimientos */}
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-lg font-semibold text-blue-800">Establecimientos (Puntos de Emisión)</h3>
                  <button
                    type="button"
                    onClick={() => setMostrarFormEstablecimiento(!mostrarFormEstablecimiento)}
                    className="bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 text-sm"
                  >
                    {mostrarFormEstablecimiento ? "Cancelar" : "+ Agregar Establecimiento"}
                  </button>
                </div>

                {/* Formulario para agregar/editar establecimiento */}
                {mostrarFormEstablecimiento && (
                  <div className="bg-white p-4 rounded border mb-3">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                      <InputGroup label="Establecimiento (3 dígitos)" error={errors.estab}>
                        <input
                          type="text"
                          value={establecimiento.estab}
                          onChange={(e) => setEstablecimiento({ ...establecimiento, estab: e.target.value })}
                          placeholder="001"
                          maxLength="3"
                          className="input-base"
                        />
                      </InputGroup>

                      <InputGroup label="Punto Emisión (3 dígitos)" error={errors.ptoemi}>
                        <input
                          type="text"
                          value={establecimiento.ptoemi}
                          onChange={(e) => setEstablecimiento({ ...establecimiento, ptoemi: e.target.value })}
                          placeholder="001"
                          maxLength="3"
                          className="input-base"
                        />
                      </InputGroup>

                      <InputGroup label="Dirección" error={errors.direstablecimiento}>
                        <input
                          type="text"
                          value={establecimiento.direstablecimiento}
                          onChange={(e) => setEstablecimiento({ ...establecimiento, direstablecimiento: e.target.value })}
                          placeholder="Calle Principal y Secundaria"
                          className="input-base"
                        />
                      </InputGroup>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <InputGroup label="Latitud (opcional)" error={errors.latitud}>
                        <input
                          type="text"
                          value={establecimiento.latitud}
                          onChange={(e) => setEstablecimiento({ ...establecimiento, latitud: e.target.value })}
                          placeholder="-1.8312"
                          className="input-base"
                        />
                        <p className="text-xs text-gray-500 mt-1">Ejemplo: -1.8312 (usa Google Maps para obtenerla)</p>
                      </InputGroup>

                      <InputGroup label="Longitud (opcional)" error={errors.longitud}>
                        <input
                          type="text"
                          value={establecimiento.longitud}
                          onChange={(e) => setEstablecimiento({ ...establecimiento, longitud: e.target.value })}
                          placeholder="-78.6156"
                          className="input-base"
                        />
                        <p className="text-xs text-gray-500 mt-1">Ejemplo: -78.6156 (usa Google Maps para obtenerla)</p>
                      </InputGroup>
                    </div>

                    <button
                      type="button"
                      onClick={agregarEstablecimiento}
                      className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 mt-3"
                    >
                      {editandoEstablecimiento !== null ? "Actualizar" : "Agregar"}
                    </button>
                  </div>
                )}

                {/* Lista de establecimientos */}
                {establecimientos.length > 0 ? (
                  <div className="space-y-2">
                    {establecimientos.map((estab, index) => (
                      <div key={index} className="bg-white p-3 rounded border">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-semibold text-blue-700">{estab.estab}-{estab.ptoemi}</span>
                              {(estab.latitud && estab.longitud) && (
                                <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
                                  📍 Con coordenadas
                                </span>
                              )}
                            </div>
                            <p className="text-gray-600 text-sm">{estab.direstablecimiento}</p>
                            {(estab.latitud && estab.longitud) && (
                              <p className="text-xs text-gray-500 mt-1">
                                Coordenadas: {estab.latitud}, {estab.longitud}
                              </p>
                            )}
                          </div>
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => editarEstablecimiento(index)}
                              className="bg-yellow-500 text-white px-2 py-1 rounded hover:bg-yellow-600 text-xs"
                            >
                              Editar
                            </button>
                            <button
                              type="button"
                              onClick={() => eliminarEstablecimiento(index)}
                              className="bg-red-500 text-white px-2 py-1 rounded hover:bg-red-600 text-xs"
                            >
                              Eliminar
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm text-center py-2">
                    No hay establecimientos agregados. Agrega al menos uno para poder emitir facturas.
                  </p>
                )}
              </div>

              {/* 🔐 Sección de Certificado Digital */}
              {editMode && restaurante.id_restaurante && (
                <div className="mt-8 p-6 bg-gray-50 rounded-lg border border-gray-200">
                  <h3 className="text-lg font-bold mb-4 text-gray-800">🔐 Certificado Digital (.p12)</h3>
                  
                  {/* Información del certificado actual */}
                  {infoCertificado?.tieneCertificado ? (
                    <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded">
                      <p className="text-green-800 font-medium">✅ Certificado configurado</p>
                      <p className="text-sm text-gray-600 mt-1">
                        <strong>Archivo:</strong> {infoCertificado.nombreArchivo}
                      </p>
                      {infoCertificado.password && (
                        <p className="text-sm text-gray-600 mt-1">
                          <strong>Contraseña:</strong> <span className="font-mono bg-gray-100 px-2 py-1 rounded">{infoCertificado.password}</span>
                        </p>
                      )}
                      <button
                        type="button"
                        onClick={handleEliminarCertificado}
                        className="mt-2 text-red-600 hover:text-red-800 text-sm underline"
                      >
                        Eliminar certificado
                      </button>
                    </div>
                  ) : (
                    <p className="text-gray-600 mb-4">⚠️ No hay certificado configurado. Las facturas usarán el certificado por defecto.</p>
                  )}

                  {/* Formulario para subir certificado (sin form anidado) */}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Archivo .p12 o .pfx
                      </label>
                      <input
                        type="file"
                        accept=".p12,.pfx"
                        onChange={(e) => setCertificadoFile(e.target.files[0])}
                        className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Contraseña del certificado
                      </label>
                      <input
                        type="password"
                        value={certificadoPassword}
                        onChange={(e) => setCertificadoPassword(e.target.value)}
                        placeholder="Contraseña del archivo .p12"
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <button
                      type="button"
                      onClick={handleSubirCertificado}
                      disabled={subiendoCertificado}
                      className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                      {subiendoCertificado ? "Subiendo..." : "Subir Certificado"}
                    </button>
                  </div>
                </div>
              )}

              {/* Botones */}
              <div className="flex gap-4 justify-center mt-6">
                <button type="submit" className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700" > {editMode ? "Actualizar" : "Crear"} </button>
                <button type="button" onClick={() => setModalOpen(false)} className="bg-gray-500 text-white px-6 py-2 rounded hover:bg-gray-600" > Cancelar </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default RestaurantePage;
